import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from 'js-cookie';
import request from '../helpers/request';

const parseBooleanCookie = (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
};

const setCookieIfPresent = (key, value, options) => {
    if (value === undefined || value === null) return;
    Cookies.set(key, value, options);
};

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ username, password }, thunkAPI) => {
        try {
            const response = await request.post('/api/v1/token/login/', { username, password });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data?.error || 'An unexpected error occurred');
        }
    }
);

export const fetchUserProfile = createAsyncThunk(
    'auth/fetchUserProfile',
    async (userId, thunkAPI) => {
        try {
            const response = await request.get(`/api/v1/users/${userId}/`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch user profile');
        }
    }
);

export const loginWithGoogle = createAsyncThunk(
    'auth/loginWithGoogle',
    async ({ code }, thunkAPI) => {
        try {
            const response = await request.post('/api/v1/auth/google/', { code });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data?.error || 'Google login failed');
        }
    }
);

export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async ({ userId, formData }, thunkAPI) => {
        try {
            const response = await request.patch(`/api/v1/users/${userId}/`, formData);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update profile');
        }
    }
);

const initialState = {
    isAuthenticated: ['token', 'refresh', 'domain', 'role', 'username', 'id'].every(cookie => Cookies.get(cookie)),
    user: {
        id: Cookies.get('id') || null,
        username: Cookies.get('username') || null,
        first_name: Cookies.get('first_name') || null,
        last_name: Cookies.get('last_name') || null,
        role: Cookies.get('role') || null,
        domain: Cookies.get('domain') || null,
        email: Cookies.get('email') || null,
        company: Cookies.get('company') ? JSON.parse(Cookies.get('company')) : null,
        agreement: Cookies.get('agreement') || null,
        agreement_declined: Cookies.get('agreement_declined') === 'true' || null,
        onboarding_completed: parseBooleanCookie(Cookies.get('onboarding_completed')),
    },
    isLoading: false,
    error: null,
    isSaving: false
};

export const clearCookies = () => {
    const cookiesToClear = ['token', 'refresh', 'domain', 'role', 'username', 'email', 'company', 'id', 'first_name', 'last_name', 'agreement', 'agreement_declined', 'onboarding_completed'];
    // Must remove with same path option that was used to set the cookie
    cookiesToClear.forEach(cookie => Cookies.remove(cookie, { path: '/' }));
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        login: (state, action) => {
            const { user_id: id, access: token, refresh, domain, role, username, email, company, first_name, last_name, agreement, onboarding_completed } = action.payload;
            // Use provided domain or default to localhost:8000 for development
            const actualDomain = domain || 'localhost';
            state.user = { id, username, role, domain: actualDomain, email, company, first_name, last_name, agreement, onboarding_completed };

            const cookieOptions = { path: '/', sameSite: 'Lax' };
            Object.entries({ id, token, refresh, domain: actualDomain, role, username, email, first_name, last_name, agreement, onboarding_completed })
                .forEach(([key, value]) => setCookieIfPresent(key, value, cookieOptions));

            if (company) {
                Cookies.set('company', JSON.stringify(company), cookieOptions);
            }

            state.isAuthenticated = true;
        },
        logout: state => {
            clearCookies();
            state.user = { id: null, username: null, role: null, domain: null, email: null, company: null, first_name: null, last_name: null, agreement: null, agreement_declined: null, onboarding_completed: null };
            state.isAuthenticated = false;
        },
        updateUser: (state, action) => {
            const { id, username, email, first_name, last_name } = action.payload;
            state.user = { 
                ...state.user, 
                id, 
                username, 
                email, 
                first_name, 
                last_name,
            };

            const cookieOptions = { path: '/', sameSite: 'Lax' };
            Object.entries({ id, username, email, first_name, last_name })
                .forEach(([key, value]) => Cookies.set(key, value, cookieOptions));
        },
        changeTier: (state, action) => {
            const { tier } = action.payload;
            state.user.company.tier = tier;
            const cookieOptions = { path: '/', sameSite: 'Lax' };
            Cookies.set("company", JSON.stringify(state.user.company), cookieOptions);
        },
        setAgreement: (state, action) => {
            const { agreement, agreement_declined } = action.payload;
            
            state.user = {
                ...state.user,
                agreement,
                agreement_declined
            };

            const cookieOptions = { path: '/', sameSite: 'Lax' };
            Cookies.set("agreement", agreement, cookieOptions);
            Cookies.set("agreement_declined", agreement_declined, cookieOptions);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                console.log('[DEBUG] Login fulfilled - payload:', action.payload);
                const { user_id: id, access: token, refresh, domain, role, username, email, company, first_name, last_name, agreement, onboarding_completed } = action.payload;
                console.log('[DEBUG] Extracted token (access):', token ? `${token.substring(0, 20)}...` : 'UNDEFINED/NULL');
                // Use provided domain or default to localhost for development
                const actualDomain = domain || 'localhost';
                state.user = { id, username, role, domain: actualDomain, email, company, first_name, last_name, agreement, onboarding_completed };
                state.isAuthenticated = true;
                state.isLoading = false;
                state.error = null;

                const cookieOptions = { path: '/', sameSite: 'Lax' };
                Object.entries({ id, token, refresh, domain: actualDomain, role, username, email, first_name, last_name, agreement, onboarding_completed })
                    .forEach(([key, value]) => {
                        if (value !== undefined && value !== null) {
                            Cookies.set(key, value, cookieOptions);
                            console.log(`[DEBUG] Cookie set: ${key} = ${typeof value === 'string' && value.length > 20 ? value.substring(0, 20) + '...' : value}`);
                        } else {
                            console.warn(`[DEBUG] Cookie NOT set for key: ${key} (value is null or undefined)`);
                        }
                    });

                if (company) {
                    Cookies.set('company', JSON.stringify(company), cookieOptions);
                }
                
                // Verify cookies were set
                console.log('[DEBUG] Verifying token cookie after set:', Cookies.get('token') ? 'EXISTS' : 'MISSING');
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(loginWithGoogle.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                const { user_id: id, access: token, refresh, domain, role, username, email, company, first_name, last_name, agreement, onboarding_completed } = action.payload;
                const actualDomain = domain || 'localhost';
                state.user = { id, username, role, domain: actualDomain, email, company, first_name, last_name, agreement, onboarding_completed };
                state.isAuthenticated = true;
                state.isLoading = false;
                state.error = null;

                const cookieOptions = { path: '/', sameSite: 'Lax' };
                Object.entries({ id, token, refresh, domain: actualDomain, role, username, email, first_name, last_name, agreement, onboarding_completed })
                    .forEach(([key, value]) => setCookieIfPresent(key, value, cookieOptions));
                if (company) Cookies.set('company', JSON.stringify(company), cookieOptions);
            })
            .addCase(loginWithGoogle.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchUserProfile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
                if (Object.prototype.hasOwnProperty.call(action.payload, 'onboarding_completed')) {
                    const cookieOptions = { path: '/', sameSite: 'Lax' };
                    setCookieIfPresent('onboarding_completed', action.payload.onboarding_completed, cookieOptions);
                }
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(updateUserProfile.pending, (state) => {
                state.isSaving = true;
                state.error = null;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
                if (Object.prototype.hasOwnProperty.call(action.payload, 'onboarding_completed')) {
                    const cookieOptions = { path: '/', sameSite: 'Lax' };
                    setCookieIfPresent('onboarding_completed', action.payload.onboarding_completed, cookieOptions);
                }
                state.isSaving = false;
                state.error = null;
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload;
            });
    }
});

export const { login, logout, updateUser, changeTier, setAgreement } = authSlice.actions;
export default authSlice.reducer;
